<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed } from 'vue';
import LaisDrawer from 'dashboard/components-next/LaisDrawer.vue';
import LaisPrimaryButton from 'dashboard/components-next/LaisPrimaryButton.vue';
import {
  AGENDA_SEND_ACTIONS,
  buildAgendaSendActions,
} from '../utils/agendaSendActionsPresenter';
import VisitQuickActionButton from './VisitQuickActionButton.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  activeVisit: { type: Object, default: () => ({}) },
  quickVisitAddress: { type: String, default: '—' },
  quickVisitDateText: { type: String, default: '—' },
  blockedReason: { type: String, default: '' },
  isBlocked: { type: Boolean, default: false },
  hasVisitorCheckIn: { type: Boolean, default: false },
  canSendIntroduction: { type: Boolean, default: false },
  canSendOffer: { type: Boolean, default: false },
  canSuggestHouse: { type: Boolean, default: false },
  canConfirm: { type: Boolean, default: true },
  canCancel: { type: Boolean, default: true },
  canReschedule: { type: Boolean, default: true },
  canFollowUp: { type: Boolean, default: true },
  canAddMore: { type: Boolean, default: true },
  introductionLabel: { type: String, default: '' },
  offerLabel: { type: String, default: '' },
  suggestHouseLabel: { type: String, default: '' },
});

const emit = defineEmits([
  'close',
  'openBlocked',
  'openVisitorInfo',
  'sendIntroduction',
  'sendOffer',
  'startSupport',
  'suggestHouse',
  'reschedule',
  'wantCancel',
  'confirm',
  'talkOwner',
  'followUp',
  'addMore',
  'openAgendaInfo',
  'openKeyHolder',
]);

const visitorName = computed(
  () =>
    props.activeVisit?.contact?.name ||
    props.activeVisit?.clientName ||
    'Cliente não informado'
);

const propertyTitle = computed(
  () => props.activeVisit?.imovel?.titulo || 'Imóvel da visita'
);

const suggestHouseActionLabel = computed(() => {
  const label = String(props.suggestHouseLabel || '').trim();
  if (!label) return 'Sugerir imóvel';
  return label.includes('sugestão') || label.includes('imóvel')
    ? label
    : `${label} sugestão de imóvel`;
});

const primaryActions = computed(() => [
  {
    label: 'Confirmar visita',
    icon: 'i-lucide-circle-check',
    event: 'confirm',
    disabled: !props.canConfirm,
  },
  {
    label: 'Cancelar visita',
    icon: 'i-lucide-calendar-x-2',
    event: 'wantCancel',
    tone: 'danger',
    disabled: !props.canCancel,
  },
  {
    label: 'Reagendar visita',
    icon: 'i-lucide-calendar-clock',
    event: 'reschedule',
    disabled: !props.canReschedule,
  },
]);

const sendActions = computed(() =>
  buildAgendaSendActions({
    visit: props.activeVisit,
    canSendIntroduction: props.canSendIntroduction,
    canSendOffer: props.canSendOffer,
    introductionLabel: props.introductionLabel,
    offerLabel: props.offerLabel,
  }).map(action => ({
    ...action,
    disabled:
      action.disabled ||
      (action.key === AGENDA_SEND_ACTIONS.VISIT_FOLLOW_UP &&
        !props.canFollowUp) ||
      (action.key === AGENDA_SEND_ACTIONS.ADD_MORE_VISITS && !props.canAddMore),
  }))
);

const infoActions = computed(() => [
  {
    label: 'Ver perfil do visitante',
    icon: 'i-lucide-user-round-search',
    event: 'openVisitorInfo',
  },
  {
    label: suggestHouseActionLabel.value,
    icon: 'i-lucide-home',
    event: 'suggestHouse',
    disabled: !props.canSuggestHouse,
  },
  {
    label: 'Responsável pelo imóvel',
    icon: 'i-lucide-user-round-check',
    event: 'openAgendaInfo',
    payload: 'responsible',
  },
  {
    label: 'Entrada e chaves',
    icon: 'i-lucide-key-round',
    event: 'openKeyHolder',
  },
  {
    label: 'Saiba mais',
    icon: 'i-lucide-info',
    event: 'openAgendaInfo',
    payload: 'learnMore',
  },
]);

const triggerAction = action => {
  if (action.disabled) return;
  switch (action.event) {
    case 'openVisitorInfo':
      emit('openVisitorInfo');
      break;
    case 'sendIntroduction':
      emit('sendIntroduction');
      break;
    case 'sendOffer':
      emit('sendOffer');
      break;
    case 'startSupport':
      emit('startSupport');
      break;
    case 'suggestHouse':
      emit('suggestHouse');
      break;
    case 'reschedule':
      emit('reschedule');
      break;
    case 'wantCancel':
      emit('wantCancel');
      break;
    case 'confirm':
      emit('confirm');
      break;
    case 'talkOwner':
      emit('talkOwner');
      break;
    case 'followUp':
      emit('followUp');
      break;
    case 'addMore':
      emit('addMore');
      break;
    case 'openAgendaInfo':
      emit('openAgendaInfo', action.payload);
      break;
    case 'openKeyHolder':
      emit('openKeyHolder');
      break;
    default:
      break;
  }
};
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <LaisDrawer
    :show="show"
    title="Ações da visita"
    side="bottom"
    size="lg"
    @close="emit('close')"
  >
    <div class="grid gap-5">
      <div
        class="rounded-lais-sm border border-lais-border bg-lais-bg-soft px-4 py-3"
      >
        <div class="flex min-w-0 items-start justify-between gap-3">
          <div class="min-w-0">
            <h3 class="truncate text-[15px] font-semibold text-lais-text">
              {{ visitorName }}
            </h3>
            <p class="mt-1 truncate text-[13px] text-lais-text-muted">
              {{ propertyTitle }}
            </p>
          </div>
          <span
            v-if="hasVisitorCheckIn"
            class="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700"
          >
            <span class="i-lucide-circle-check size-4" aria-hidden="true" />
            Check-in realizado
          </span>
        </div>
        <dl class="mt-3 grid gap-2 text-[13px] text-lais-text-body">
          <div class="grid gap-0.5">
            <dt class="font-semibold text-lais-text">Endereço</dt>
            <dd>{{ quickVisitAddress }}</dd>
          </div>
          <div class="grid gap-0.5">
            <dt class="font-semibold text-lais-text">Data</dt>
            <dd>{{ quickVisitDateText }}</dd>
          </div>
        </dl>
      </div>

      <div
        v-if="isBlocked"
        class="flex flex-col gap-3 rounded-lais border border-orange-200 bg-orange-50 px-4 py-3 text-[13px] text-orange-900 sm:flex-row sm:items-center sm:justify-between"
      >
        <div class="min-w-0">
          <p class="font-semibold">Visita bloqueada</p>
          <p class="mt-1 leading-relaxed">
            {{ blockedReason }}
          </p>
        </div>
        <LaisPrimaryButton
          variant="ghost"
          size="sm"
          icon="i-lucide-calendar-x-2"
          @click="emit('openBlocked')"
        >
          Ver opções
        </LaisPrimaryButton>
      </div>

      <section class="grid gap-2">
        <h4 class="text-[12px] font-bold uppercase text-lais-text-muted">
          Ações principais
        </h4>
        <div class="grid gap-2 sm:grid-cols-3">
          <VisitQuickActionButton
            v-for="action in primaryActions"
            :key="action.event"
            :label="action.label"
            :icon="action.icon"
            :tone="action.tone"
            :disabled="action.disabled"
            @click="triggerAction(action)"
          />
        </div>
      </section>

      <section class="grid gap-2">
        <h4 class="text-[12px] font-bold uppercase text-lais-text-muted">
          Enviar e acompanhar
        </h4>
        <div class="grid gap-2 sm:grid-cols-2">
          <VisitQuickActionButton
            v-for="action in sendActions"
            :key="action.event"
            :label="action.label"
            :icon="action.icon"
            :disabled="action.disabled"
            @click="triggerAction(action)"
          />
        </div>
      </section>

      <section class="grid gap-2">
        <h4 class="text-[12px] font-bold uppercase text-lais-text-muted">
          Informações
        </h4>
        <div class="grid gap-2 sm:grid-cols-2">
          <VisitQuickActionButton
            v-for="action in infoActions"
            :key="`${action.event}-${action.payload || 'default'}`"
            :label="action.label"
            :icon="action.icon"
            :disabled="action.disabled"
            @click="triggerAction(action)"
          />
        </div>
      </section>
    </div>
  </LaisDrawer>
</template>
